import { formatDate, formatYYMMDD, } from "@erp/shared/utils/date/formatted-date";
import { useMember } from "@erp/shared/components/ui/Dialog/MemberSearchDialog/useMember";
import useEditMode from "@erp/shared/components/ui/Document/_composable/useEditMode";
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { ESTIMATE_BASIC_INFO, } from "../EstimateWriteTab.types";
import { defaultMemberInfo } from "@erp/shared/components/ui/Dialog/MemberSearchDialog/member-info.const";
import { estimateDefaultInfo, estimateDefaultInfo2, estimateFilesInputs, } from "../EstimateWriteTab.const";
import { findByKey } from "@erp/shared/utils/getProperty";
import { convertToFileObj } from "@erp/shared/utils/file/convert-to-file-obj";
import { useLoginInfo } from "@erp/shared/composables/useLoginInfo";
import { useERP } from "@erp/shared/composables/useERP";
import { checkEstimateDuplicate } from "@erp/shared/api/generated/wrappers/estimate";
import { getEstimateConfirm } from "@erp/shared/api/generated/wrappers/estimate";
import { useCompanyAutofill } from "@erp/shared/composables/useCompanyAutofill";
export const useEstimateBasicInfoStore = defineStore("estimateBasicInfo", () => {
    const { userData } = useLoginInfo();
    const confirmId = ref(undefined);
    const member = useMember(defaultMemberInfo, { setChecker: true }, userData);
    const edit = useEditMode();
    const { isEdit } = edit;
    const basicInfoValidate = ref(false);
    const writeIssueDate = ref(formatDate());
    const { codeInfo } = useERP();
    const basicInfoFirstInputs = ref(estimateDefaultInfo(codeInfo.inhouseBidNumberPrefix));
    const basicInfoSecondInputs = ref(estimateDefaultInfo2);
    const filesInputs = ref(estimateFilesInputs);
    // 업체+담당자 자동 채우기
    const companyAutofill = useCompanyAutofill();
    const inputIssueDate = computed(() => findByKey(basicInfoFirstInputs.value, "label", ESTIMATE_BASIC_INFO.발행일));
    const inputInhouseBidNumber = computed(() => findByKey(basicInfoFirstInputs.value, "label", ESTIMATE_BASIC_INFO.사내견적번호));
    const inputCompanyBidNumber = computed(() => findByKey(basicInfoFirstInputs.value, "label", ESTIMATE_BASIC_INFO.기업별입찰번호));
    // 기본 정보(두 번째 그룹)
    const inputDueDate = computed(() => findByKey(basicInfoSecondInputs.value, "label", ESTIMATE_BASIC_INFO.납기일));
    const inputProjectName = computed(() => findByKey(basicInfoSecondInputs.value, "label", ESTIMATE_BASIC_INFO.견적명));
    const inputProjectPeriod = computed(() => findByKey(basicInfoSecondInputs.value, "label", ESTIMATE_BASIC_INFO.견적기간));
    const inputRemark = computed(() => findByKey(basicInfoSecondInputs.value, "label", ESTIMATE_BASIC_INFO.특이사항));
    const inputApprovalFile = computed(() => findByKey(filesInputs.value, "label", ESTIMATE_BASIC_INFO.승인서) ?? null);
    const inputBlueprintFile = computed(() => findByKey(filesInputs.value, "label", ESTIMATE_BASIC_INFO.도면) ?? null);
    const inputOtherFiles = computed(() => findByKey(filesInputs.value, "label", ESTIMATE_BASIC_INFO.기타) ?? null);
    const setWriteIssueDate = (newDate) => {
        writeIssueDate.value = newDate;
    };
    const setBasicInfoFirstInputs = (inputs) => {
        basicInfoFirstInputs.value = inputs;
    };
    const setBasicInfoSecondInputs = (inputs) => {
        basicInfoSecondInputs.value = inputs;
    };
    const setFilesInputs = (inputs) => {
        filesInputs.value = inputs;
    };
    const setBasicInfoValidate = (value) => {
        basicInfoValidate.value = value;
    };
    /** 새 문서 작성 시 업체/담당자 및 store 상태 초기화 */
    const resetBasicInfo = () => {
        confirmId.value = undefined;
        basicInfoFirstInputs.value = estimateDefaultInfo(codeInfo.inhouseBidNumberPrefix);
        basicInfoSecondInputs.value = [...estimateDefaultInfo2];
        filesInputs.value = [...estimateFilesInputs];
        companyAutofill.reset();
        initChipSelectOptions();
    };
    const applyBasicInfo = async (data) => {
        if (!data) {
            return;
        }
        // 이전 문서 데이터 초기화
        companyAutofill.reset();
        if (isEdit.value) {
            confirmId.value = data.info.id;
        }
        if (inputIssueDate.value) {
            const issueDate = data.info.createdTime.split("T")[0];
            inputIssueDate.value.value = issueDate;
            writeIssueDate.value = issueDate;
        }
        if (inputInhouseBidNumber.value) {
            inputInhouseBidNumber.value.value = isEdit.value
                ? (data.info.inhouseBidNumber ?? "")
                : codeInfo.inhouseBidNumberPrefix + formatYYMMDD();
            if ("chipMeta" in inputInhouseBidNumber.value) {
                inputInhouseBidNumber.value.chipMeta = {
                    mode: isEdit.value ? "manual" : "auto",
                    selectedId: isEdit.value ? data.info.id : undefined,
                };
            }
        }
        if (inputCompanyBidNumber.value) {
            inputCompanyBidNumber.value.value = data.info.companyBidNumber ?? "";
        }
        if (inputDueDate.value) {
            inputDueDate.value.value = formatDate(data.info.dueDate) ?? "";
        }
        if (inputProjectName.value) {
            inputProjectName.value.value = data.info.projectName ?? "";
        }
        if (inputProjectPeriod.value) {
            inputProjectPeriod.value.value = data.info.projectPeriod ?? "";
        }
        if (inputRemark.value) {
            inputRemark.value.value = data.info.remark ?? "";
        }
        // 업체+담당자 자동 채우기 초기화
        await companyAutofill.initFromExisting({
            companyId: data.info.companyId,
            companyName: data.info.companyName ?? "",
            managerId: data.info.managerId,
            managerName: data.info.managerName ?? "",
            managerEmail: data.info.managerEmail ?? "",
            managerPhone: data.info.managerPhone ?? "",
        });
        if (inputApprovalFile.value && data.info.approvalFile) {
            const file = await convertToFileObj(data.info.approvalFile);
            inputApprovalFile.value.value = file;
        }
        if (inputBlueprintFile.value && data.info.blueprintFile) {
            const file = await convertToFileObj(data.info.blueprintFile);
            inputBlueprintFile.value.value = file;
        }
        if (inputOtherFiles.value && data.info.etcFiles) {
            if (typeof data.info.etcFiles === "string") {
                data.info.etcFiles = JSON.parse(data.info.etcFiles);
            }
            if (data.info.etcFiles && data.info.etcFiles.length > 0) {
                const names = data.info.etcFiles;
                const files = (await Promise.all(names.map((name) => convertToFileObj(name)))).filter((f) => !!f);
                inputOtherFiles.value.value = files;
            }
            else {
                inputOtherFiles.value.value = null;
            }
        }
    };
    // === 사내 견적번호 chipSelect 검색/중복검사 함수 바인딩 ===
    const initChipSelectOptions = () => {
        const input = inputInhouseBidNumber.value;
        if (input && "chipOptions" in input) {
            input.chipOptions.searchFn = async (keyword) => {
                const searchInputs = [
                    {
                        label: "사내 견적번호",
                        key: "inhouseBidNumber",
                        col: "12",
                        sm: "3",
                        lg: "3",
                        value: keyword,
                    },
                ];
                const results = await getEstimateConfirm(searchInputs);
                return results.map((r) => ({
                    id: r.id,
                    displayValue: r.inhouseBidNumber,
                    companyName: r.companyName,
                    approvalPhase: r.approvalPhase,
                }));
            };
            input.chipOptions.duplicateCheckFn = checkEstimateDuplicate;
        }
    };
    initChipSelectOptions();
    return {
        ...member,
        ...edit,
        basicInfoValidate,
        /** 작성 날짜 */
        writeIssueDate,
        /** 견적서 정보 상단 입력  */
        basicInfoFirstInputs,
        /** 견적서 정보 하단 입력  */
        basicInfoSecondInputs,
        /** 업체+담당자 자동 채우기 */
        companyAutofill,
        /** 첨부 입력  */
        filesInputs,
        inputIssueDate,
        inputInhouseBidNumber,
        inputCompanyBidNumber,
        inputDueDate,
        inputProjectName,
        inputProjectPeriod,
        inputRemark,
        inputApprovalFile,
        inputBlueprintFile,
        inputOtherFiles,
        confirmId,
        // setters
        setWriteIssueDate,
        setBasicInfoValidate,
        setBasicInfoFirstInputs,
        setBasicInfoSecondInputs,
        setFilesInputs,
        applyBasicInfo,
        resetBasicInfo,
    };
});
